import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import ErrorBanner from '@/components/ErrorBanner'
import StatusMessage from '@/components/StatusMessage'
import { useApplication } from '@/hooks/useApplication'
import {
  applicationSchema,
  DEFAULT_FORM_VALUES,
  stripMoneyFormatting,
  type ApplicationFormData,
} from '@/models/applicationSchema'
import ApplicationForm from '@/pages/ApplicationForm'

interface ApplicationPageProps {
  recordId: string | null
}

function ApplicationPage({ recordId }: ApplicationPageProps) {
  const { state, submitError, errorId, serverErrors, submit, dismissError } =
    useApplication(recordId)

  const form = useForm<ApplicationFormData>({
    resolver: zodResolver(applicationSchema),
    // FormDefaults widens state fields to string for empty defaults; cast is safe because
    // Zod validation rejects invalid states before handleSubmit fires.
    defaultValues: DEFAULT_FORM_VALUES as unknown as ApplicationFormData,
  })

  useEffect(() => {
    if (!serverErrors) return
    Object.entries(serverErrors).forEach(([field, message]) => {
      form.setError(field as keyof ApplicationFormData, { message })
    })
  }, [serverErrors, form])

  if (state === 'no-id') {
    return (
      <StatusMessage
        message="Invalid or Missing Credit App ID. Please contact your Sales Rep in order to apply for credit."
        variant="error"
      />
    )
  }
  if (state === 'loading') {
    return <StatusMessage message="Loading application..." variant="loading" />
  }
  if (state === 'not-found') {
    return <StatusMessage message="Application not found." variant="error" />
  }
  if (state === 'already-submitted') {
    return <StatusMessage message="This application has already been submitted." variant="info" />
  }
  if (state === 'server-error') {
    return (
      <StatusMessage
        message="Something went wrong. Please try again later."
        variant="error"
        errorId={errorId}
      />
    )
  }
  if (state === 'status-changed') {
    return (
      <StatusMessage
        message="This application can no longer be submitted. It may have already been processed."
        variant="info"
      />
    )
  }
  if (state === 'success') {
    return (
      <StatusMessage
        message="Your credit application has been submitted successfully. You will be contacted regarding your application."
        variant="success"
      />
    )
  }

  const isDisabled = state === 'submitting'

  return (
    <div className="relative">
      {submitError ? (
        <ErrorBanner message={submitError} errorId={errorId} onDismiss={dismissError} />
      ) : null}
      {isDisabled ? <LoadingOverlay /> : null}
      <ApplicationForm
        form={form}
        onSubmit={(data) => submit(stripMoneyFormatting(data))}
        isDisabled={isDisabled}
      />
    </div>
  )
}

function LoadingOverlay() {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/70">
      <div className="rounded-lg bg-white p-6 shadow-lg">
        <p className="text-gray-700">Submitting your application...</p>
      </div>
    </div>
  )
}

export default ApplicationPage
